import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, AlertCircle, Building, CheckCircle, Clock, ExternalLink, Home, Image as ImageIcon, Mail, MapPin, Phone, PlayCircle, Shield, Star, Train, Upload, User, Video, X, XCircle } from 'lucide-react'
import { motion } from 'framer-motion'
import { yogaSchoolService, formatDate, formatRelativeTime } from '../services/yogaSchoolService'
import VerificationDocumentsList from '../components/VerificationDocumentsList'

type SchoolRecord = Record<string, any>

const excludedTopLevelKeys = new Set([
  '__v',
  'images',
  'videos',
  'verificationDocuments',
  'verificationDocumentTypes',
  'verificationMeta',
  'howToReach',
])

const formatLabel = (key: string) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeMediaUrl = (value?: string) => {
  if (!value) return ''
  if (value.startsWith('http')) return value
  const base = import.meta.env.VITE_MEDIA_BASE_URL
  if (!base) return value
  const cleanBase = base.replace(/\/+$/, '')
  const cleanValue = value.replace(/^\/+/, '')
  return `${cleanBase}/${cleanValue}`
}

const getDocInfo = (doc: unknown) => {
  if (typeof doc === 'string') {
    return { url: normalizeMediaUrl(doc), type: 'additional' }
  }
  if (isPlainObject(doc)) {
    return {
      url: normalizeMediaUrl(String(doc.url || '')),
      type: String(doc.type || 'additional'),
    }
  }
  return { url: '', type: 'additional' }
}

const getModeLabel = (mode: string) => {
  const labels: Record<string, string> = {
    TRAIN: 'Train',
    BUS: 'Bus',
    TAXI: 'Taxi',
    AIR: 'Air',
  }
  return labels[mode] || mode
}

const getModeColor = (mode: string) => {
  const colors: Record<string, string> = {
    TRAIN: 'bg-blue-100 text-blue-800',
    BUS: 'bg-emerald-100 text-emerald-800',
    TAXI: 'bg-amber-100 text-amber-800',
    AIR: 'bg-purple-100 text-purple-800',
  }
  return colors[mode] || 'bg-gray-100 text-gray-800'
}

const roomTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    PRIVATE: 'Private',
    DOUBLE: 'Double',
    TRIPLE: 'Triple',
    QUADRUPLE: 'Quadruple',
    CUSTOM: 'Custom',
  }
  return labels[type] || type
}

const MediaCard = ({
  title,
  imageUrl,
  onOpen,
}: {
  title: string
  imageUrl?: string
  onOpen?: (url: string) => void
}) => {
  if (!imageUrl) return null
  return (
    <button
      type="button"
      onClick={() => onOpen?.(imageUrl)}
      className="group overflow-hidden rounded-xl border border-gray-200 bg-white text-left"
    >
      <div className="aspect-[4/3] bg-gray-100">
        <img src={imageUrl} alt={title} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]" />
      </div>
      <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-3">
        <ImageIcon className="h-4 w-4 text-amber-600" />
        <span className="text-sm font-medium text-gray-900">{title}</span>
      </div>
    </button>
  )
}

const ValueBlock = ({ value }: { value: unknown }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400">-</span>
  }

  if (typeof value === 'boolean') {
    return <span className={value ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>{value ? 'Yes' : 'No'}</span>
  }

  if (typeof value === 'string') {
    if (/^https?:\/\//i.test(value)) {
      return (
        <a href={value} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-amber-700 hover:underline break-all">
          {value}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )
    }
    return <span className="break-words">{value}</span>
  }

  if (typeof value === 'number') {
    return <span>{String(value)}</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-gray-400">-</span>
    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            {isPlainObject(item) || Array.isArray(item) ? (
              <ValueBlock value={item} />
            ) : (
              <span>{String(item)}</span>
            )}
          </div>
        ))}
      </div>
    )
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value)
    if (entries.length === 0) return <span className="text-gray-400">-</span>
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {entries.map(([key, nestedValue]) => (
          <div key={key} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{formatLabel(key)}</p>
            <div className="mt-1 text-sm text-gray-900">
              <ValueBlock value={nestedValue} />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return <span>{String(value)}</span>
}

const YogaSchoolDetail = () => {
  const { schoolId } = useParams<{ schoolId: string }>()
  const navigate = useNavigate()
  const [school, setSchool] = useState<SchoolRecord | null>(null)
  const [rooms, setRooms] = useState<SchoolRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  useEffect(() => {
    if (!schoolId) return
    const fetchSchool = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await yogaSchoolService.getYogaSchoolById(schoolId)
        const schoolData = (response as any)?.data?.school ?? (response as any)?.data ?? null
        const roomsPayload = (response as any)?.data?.rooms
        if (!response?.success || !schoolData) {
          setError('Yoga school not found')
          setRooms([])
          return
        }
        setSchool(schoolData)
        setRooms(Array.isArray(roomsPayload) ? roomsPayload : [])
      } catch (err: any) {
        setError(err?.message || 'Failed to load yoga school details')
      } finally {
        setLoading(false)
      }
    }
    fetchSchool()
  }, [schoolId])

  const owner = useMemo(() => {
    if (!school?.ownerId || typeof school.ownerId === 'string') return null
    return school.ownerId as Record<string, unknown>
  }, [school])

  const topLevelEntries = useMemo(() => {
    if (!school) return []
    return Object.entries(school).filter(([key]) => !excludedTopLevelKeys.has(key))
  }, [school])

  const images = useMemo(() => {
    const imageData = school?.images
    if (!isPlainObject(imageData)) return { logo: '', coverImage: '', gallery: [] as string[] }
    return {
      logo: typeof imageData.logo === 'string' ? normalizeMediaUrl(imageData.logo) : '',
      coverImage: typeof imageData.coverImage === 'string' ? normalizeMediaUrl(imageData.coverImage) : '',
      gallery: Array.isArray(imageData.gallery)
        ? imageData.gallery.filter((item): item is string => typeof item === 'string').map((item) => normalizeMediaUrl(item))
        : [],
    }
  }, [school])

  const videos = useMemo(() => {
    const videoData = school?.videos
    if (!isPlainObject(videoData)) return { introVideo: '', gallery: [] as string[] }
    return {
      introVideo: typeof videoData.introVideo === 'string' ? normalizeMediaUrl(videoData.introVideo) : '',
      gallery: Array.isArray(videoData.gallery)
        ? videoData.gallery.filter((item): item is string => typeof item === 'string').map((item) => normalizeMediaUrl(item))
        : [],
    }
  }, [school])

  const verificationDocuments = useMemo(
    () => (Array.isArray(school?.verificationDocuments) ? school.verificationDocuments.map(getDocInfo).filter((doc) => doc.url) : []),
    [school]
  )

  const howToReachStations = useMemo(() => {
    const raw = school?.howToReach
    if (!isPlainObject(raw) || !Array.isArray(raw.stations)) return []
    return raw.stations.filter((station): station is { name?: string; options?: Array<{ mode?: string; details?: string }> } => isPlainObject(station))
  }, [school])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500" />
      </div>
    )
  }

  if (error || !school) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600 mb-4">{error || 'Yoga school not found'}</p>
        <button
          onClick={() => navigate('/admin/yoga-schools')}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
        >
          Back to Yoga Schools
        </button>
      </div>
    )
  }

  const approvalStatus = school.approvalStatus || 'PENDING'
  const verificationStatus = school.verificationStatus || 'PENDING'

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{school.name || 'Yoga School'}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {school.city || school.location || '-'} {school.country ? `• ${school.country}` : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            type="button"
            onClick={() => navigate(`/admin/yoga-schools/${schoolId}/verification-documents`)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 transition-colors shadow-sm"
          >
            <Upload className="h-4 w-4" />
            Upload verification docs
          </button>
          <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${school.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {school.isActive ? <CheckCircle className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
            {`Account Status: ${school.isActive ? 'Active' : 'Inactive'}`}
          </span>
          <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${approvalStatus === 'APPROVED' ? 'bg-green-100 text-green-800' : approvalStatus === 'REJECTED' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-700'}`}>
            {approvalStatus === 'APPROVED' ? <CheckCircle className="h-4 w-4 mr-2" /> : approvalStatus === 'REJECTED' ? <XCircle className="h-4 w-4 mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
            {`School Approval: ${approvalStatus}`}
          </span>
          <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${verificationStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' : verificationStatus === 'REJECTED' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-700'}`}>
            {verificationStatus === 'VERIFIED' ? <CheckCircle className="h-4 w-4 mr-2" /> : verificationStatus === 'REJECTED' ? <XCircle className="h-4 w-4 mr-2" /> : <Clock className="h-4 w-4 mr-2" />}
            {`Verification Status: ${verificationStatus}`}
          </span>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-amber-500" />
              Overview
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-500">Slug</p>
                <p className="font-medium text-gray-900 mt-1">{school.slug || '-'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-500">Rating</p>
                <p className="font-medium text-gray-900 mt-1 inline-flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  {school.rating ?? 0} / 5 ({school.totalReviews ?? 0} reviews)
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium text-gray-900 mt-1">{school.createdAt ? formatDate(school.createdAt) : '-'}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <p className="text-sm text-gray-500">Updated</p>
                <p className="font-medium text-gray-900 mt-1">{school.updatedAt ? formatRelativeTime(school.updatedAt) : '-'}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm text-gray-500">Description</p>
              <p className="text-gray-900 whitespace-pre-wrap mt-1">{school.description || '-'}</p>
            </div>
            {school.shortDescription && (
              <div className="mt-4">
                <p className="text-sm text-gray-500">Short Description</p>
                <p className="text-gray-900 whitespace-pre-wrap mt-1">{school.shortDescription}</p>
              </div>
            )}
          </section>

          {(images.logo || images.coverImage || images.gallery.length > 0) && (
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-amber-500" />
                Media
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <MediaCard title="Logo" imageUrl={images.logo} onOpen={setPreviewImage} />
                <MediaCard title="Cover Image" imageUrl={images.coverImage} onOpen={setPreviewImage} />
              </div>
              {images.gallery.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-600 mb-3">Gallery</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {images.gallery.map((imageUrl, index) => (
                      <MediaCard key={`${imageUrl}-${index}`} title={`Gallery Image ${index + 1}`} imageUrl={imageUrl} onOpen={setPreviewImage} />
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {(videos.introVideo || videos.gallery.length > 0) && (
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Video className="w-5 h-5 text-amber-500" />
                Videos
              </h2>
              <div className="space-y-4">
                {videos.introVideo && (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <div className="aspect-video bg-black">
                      <video src={videos.introVideo} controls className="h-full w-full" />
                    </div>
                    <div className="px-4 py-3 bg-white flex items-center gap-2">
                      <PlayCircle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-gray-900">Intro Video</span>
                    </div>
                  </div>
                )}
                {videos.gallery.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-3">Video Gallery</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {videos.gallery.map((videoUrl, index) => (
                        <div key={`${videoUrl}-${index}`} className="rounded-xl border border-gray-200 overflow-hidden">
                          <div className="aspect-video bg-black">
                            <video src={videoUrl} controls className="h-full w-full" />
                          </div>
                          <div className="px-4 py-3 bg-white flex items-center gap-2">
                            <PlayCircle className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-medium text-gray-900">{`Video ${index + 1}`}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Home className="w-5 h-5 text-amber-500" />
              Room types &amp; media
            </h2>
            {rooms.length === 0 ? (
              <p className="text-sm text-gray-500">No room types registered for this school.</p>
            ) : (
              <div className="space-y-6">
                {rooms.map((room, idx) => {
                  const rType = typeof room.type === 'string' ? room.type : ''
                  const rName = typeof room.name === 'string' ? room.name : `Room ${idx + 1}`
                  const capacity = typeof room.capacity === 'number' ? room.capacity : null
                  const notes = typeof room.notes === 'string' ? room.notes : ''
                  const active = room.isActive !== false
                  const imgList = Array.isArray(room.images)
                    ? room.images.filter((u): u is string => typeof u === 'string').map((u) => normalizeMediaUrl(u))
                    : []
                  const vid = typeof room.video === 'string' && room.video ? normalizeMediaUrl(room.video) : ''

                  return (
                    <div
                      key={String(room._id ?? idx)}
                      className="rounded-xl border border-gray-200 bg-gray-50/80 p-5 space-y-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-900">
                              {roomTypeLabel(rType)}
                            </span>
                            {!active && (
                              <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                Inactive
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mt-2">{rName}</h3>
                          {capacity != null && (
                            <p className="text-sm text-gray-600 mt-1">Capacity: {capacity}</p>
                          )}
                          {notes ? (
                            <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{notes}</p>
                          ) : null}
                        </div>
                      </div>
                      {imgList.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-600 mb-3">Room images</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {imgList.map((url, i) => (
                              <MediaCard key={`${url}-${i}`} title={`Image ${i + 1}`} imageUrl={url} onOpen={setPreviewImage} />
                            ))}
                          </div>
                        </div>
                      )}
                      {vid ? (
                        <div>
                          <p className="text-sm font-medium text-gray-600 mb-2">Room video</p>
                          <div className="rounded-xl border border-gray-200 overflow-hidden max-w-xl">
                            <div className="aspect-video bg-black">
                              <video src={vid} controls className="h-full w-full" />
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            )}
          </section>

          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-500" />
                  Verification Documents
                </h2>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/yoga-schools/${schoolId}/verification-documents`)}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
                >
                  <Upload className="h-4 w-4" />
                  Add documents for school
                </button>
              </div>
              {!(school.verificationMeta || verificationDocuments.length > 0) && (
                <p className="text-sm text-gray-500 mb-4">No verification documents on file yet. Upload documents the school sent by email.</p>
              )}
              {school.verificationMeta && isPlainObject(school.verificationMeta) && Object.keys(school.verificationMeta).length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                  {Object.entries(school.verificationMeta).map(([key, value]) => (
                    <div key={key} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{formatLabel(key)}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">{String(value || '-')}</p>
                    </div>
                  ))}
                </div>
              )}
              <VerificationDocumentsList
                documents={verificationDocuments}
                emptyMessage="No verification documents on file yet. Upload documents the school sent by email."
              />
            </section>

          {howToReachStations.length > 0 && (
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Train className="w-5 h-5 text-amber-500" />
                How To Reach
              </h2>
              <div className="space-y-4">
                {howToReachStations.map((station, index) => (
                  <div key={`${station.name || 'station'}-${index}`} className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Arrival Point</p>
                        <h3 className="text-lg font-semibold text-gray-900 mt-1">{station.name || `Station ${index + 1}`}</h3>
                      </div>
                      <span className="text-sm text-gray-500">
                        {Array.isArray(station.options) ? `${station.options.length} transport option${station.options.length === 1 ? '' : 's'}` : 'No transport options'}
                      </span>
                    </div>
                    {Array.isArray(station.options) && station.options.length > 0 && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {station.options.map((option, optionIndex) => (
                          <div key={`${station.name || index}-${optionIndex}`} className="rounded-lg border border-white bg-white p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getModeColor(String(option.mode || ''))}`}>
                                {getModeLabel(String(option.mode || ''))}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{option.details || 'No details provided.'}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">All Stored Fields</h2>
            <div className="space-y-4">
              {topLevelEntries.map(([key, value]) => (
                <div key={key} className="border border-gray-200 rounded-xl p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{formatLabel(key)}</p>
                  <div className="text-sm text-gray-900">
                    <ValueBlock value={value} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-500" />
              Contact & Location
            </h2>
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-gray-400" />
                <span>{school.location || `${school.city || '-'}, ${school.country || '-'}`}</span>
              </div>
              {school.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-gray-400" />
                  <span>{school.address}</span>
                </div>
              )}
              {school.phone && (
                <div className="flex items-start gap-2">
                  <Phone className="w-4 h-4 mt-0.5 text-gray-400" />
                  <span>{school.phone}</span>
                </div>
              )}
              {school.email && (
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-0.5 text-gray-400" />
                  <span>{school.email}</span>
                </div>
              )}
              {school.website && (
                <div className="flex items-start gap-2">
                  <ExternalLink className="w-4 h-4 mt-0.5 text-gray-400" />
                  <a href={school.website} target="_blank" rel="noreferrer" className="text-amber-700 hover:underline break-all">{school.website}</a>
                </div>
              )}
            </div>
          </section>

          <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-amber-500" />
              Owner Details
            </h2>
            {owner ? (
              <div className="space-y-3 text-sm text-gray-700">
                <div>
                  <p className="text-gray-500">Name</p>
                  <p className="font-medium text-gray-900">{`${owner.firstName || ''} ${owner.lastName || ''}`.trim() || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{String(owner.email || '-')}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">{String(owner.phone || '-')}</p>
                </div>
                <div>
                  <p className="text-gray-500">Role</p>
                  <p className="font-medium text-gray-900">{String(owner.role || '-')}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No owner details available.</p>
            )}
          </section>
        </div>
      </motion.div>

      {previewImage && (
        <div className="fixed inset-0 z-[100] bg-black/80 p-4 flex items-center justify-center" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-6xl max-h-[90vh] w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute right-2 top-2 z-10 rounded-full bg-white/90 p-2 text-gray-700 hover:bg-white"
            >
              <X className="h-5 w-5" />
            </button>
            <img src={previewImage} alt="Preview" className="max-h-[90vh] max-w-full object-contain rounded-xl" />
          </div>
        </div>
      )}
    </div>
  )
}

export default YogaSchoolDetail
